/**
 * VehicleMappingPanel — GENERALISED Atlas → Ops vehicle mapping panel.
 *
 * A reusable, flow-agnostic panel for the fixed-route deploy experience (PRD
 * `FeaturePlans/OpsUpload/prd-fixed-route-vehicles-and-operations.md`, Phase 1
 * items 4 & 6 / GAP D / Modal 1). It is the de-ELC-ified successor to
 * `OpsVehicleMappingPanel`: same auth → network → read-only `getvehicles` load,
 * same slot → `opsVehicleId` mapping, but
 *   - ELC DAY/LATE/NIGHT presets are OPTIONAL and PLUGGABLE (passed via props;
 *     default = none), so arbitrary networks are first-class;
 *   - it auto-matches slots to Ops vehicles by rego / name / Ops uuid;
 *   - the mapping is persisted to localStorage so it survives reload (INTERIM —
 *     see `vehicleMappingStore.ts`; replaced by the Ops external-reference field
 *     in Phase 2);
 *   - saving emits the `fixed_route_vehicle_mapping_saved` analytics event.
 *
 * Per repo convention every categorical/list/settings label leads with a
 * semantic MUI (Outlined) icon.
 *
 * The existing `OpsVehicleMappingPanel` (Postcode/ELC) is intentionally left
 * untouched; this component does not replace it.
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme, type SxProps, type Theme } from '@mui/material/styles';
import AddOutlined from '@mui/icons-material/AddOutlined';
import AutoFixHighOutlined from '@mui/icons-material/AutoFixHighOutlined';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import DirectionsBusOutlined from '@mui/icons-material/DirectionsBusOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import HubOutlined from '@mui/icons-material/HubOutlined';
import SaveOutlined from '@mui/icons-material/SaveOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';

import {
  fetchNetworks,
  type Environment,
  type UATNetwork,
} from '../../services/fixedRouteUatClient';
import {
  ENVIRONMENT_OPTIONS,
  getEnvironmentDisplayName,
  getEnvironmentTokenStorageKey,
} from '../../services/environmentConfig';
import { fetchOpsVehiclesForNetwork } from '../../services/postcodeAssignment/opsVehicleAdapter';
import { trackAnalyticsEvent } from '../../services/analytics';
import {
  autoMatchSlotsToVehicles,
  buildEmptyVehicleSlot,
  describeOpsVehicle,
  mappingKeyForSlot,
  type OpsVehicle,
  type VehicleSlot,
  type VehicleSlotPreset,
} from '../../services/fixedRoute/vehicleMappingTypes';
import {
  getVehicleMapping,
  setVehicleMapping,
} from '../../services/fixedRoute/vehicleMappingStore';

// ─── Props ────────────────────────────────────────────────────────────────────

/**
 * Externally-supplied auth + network selection. When provided, the panel runs in
 * "embedded" mode: it hides its own JWT / environment / network selectors and
 * loads vehicles for the parent's selection automatically — so the user sets the
 * environment + network once (e.g. in AttachOperationsModal) and it drives both
 * operations and vehicles.
 */
export interface VehicleMappingAuth {
  jwtToken: string;
  environment: Environment;
  productId: string;
  /** Network UUID, or the dbId as a string when the UUID resolves on load. */
  networkUuid: string;
  networkLabel?: string;
}

export interface VehicleMappingPanelProps {
  /**
   * Optional pluggable slot presets (e.g. ELC bands, or one-slot-per-route).
   * Each renders an "apply" button that seeds the slot list. Default: none —
   * the panel is ELC-agnostic.
   */
  presets?: VehicleSlotPreset[];
  /**
   * Optional controlled slot state. When provided with `onSlotsChange`, the
   * parent owns the slots; otherwise the panel manages them internally.
   */
  slots?: VehicleSlot[];
  /** Change handler for controlled mode; also fires in uncontrolled mode. */
  onSlotsChange?: (slots: VehicleSlot[]) => void;
  /** Pre-select an environment (default 'UAT'). */
  defaultEnvironment?: Environment;
  /** Notified after a successful Save (mapping persisted to localStorage). */
  onMappingSaved?: (info: {
    environment: Environment;
    networkId: string;
    mappedCount: number;
    unmappedCount: number;
  }) => void;
  /**
   * When supplied, the panel runs embedded against the parent's auth + network
   * (see {@link VehicleMappingAuth}) and hides its own credential/network UI.
   */
  auth?: VehicleMappingAuth;
  /**
   * Optional z-index override for this panel's `<Select>` dropdown popups,
   * applied via `MenuProps={{ sx }}`. Supply this when the panel is embedded
   * inside a Dialog hoisted above MUI's default modal layer (e.g.
   * AttachOperationsModal's 2600 dialog) so the slot dropdowns render ABOVE the
   * dialog instead of behind it. Keep the value clearly above the host dialog's
   * z-index. When omitted, MUI's default (`theme.zIndex.modal`, 1300) applies.
   */
  menuZIndexSx?: SxProps<Theme>;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const VehicleMappingPanel: React.FC<VehicleMappingPanelProps> = ({
  presets = [],
  slots: controlledSlots,
  onSlotsChange,
  defaultEnvironment = 'UAT',
  onMappingSaved,
  auth,
  menuZIndexSx,
}) => {
  const theme = useTheme();
  const embedded = auth !== undefined;

  // Slots: controlled if a `slots` prop is supplied, else internal.
  const [internalSlots, setInternalSlots] = React.useState<VehicleSlot[]>([]);
  const isControlled = controlledSlots !== undefined;
  const slots = isControlled ? (controlledSlots as VehicleSlot[]) : internalSlots;

  const setSlots = React.useCallback(
    (next: VehicleSlot[]) => {
      if (!isControlled) setInternalSlots(next);
      onSlotsChange?.(next);
    },
    [isControlled, onSlotsChange],
  );

  // Auth
  const [jwtToken, setJwtToken] = React.useState('');
  const [showJwt, setShowJwt] = React.useState(false);
  const [environment, setEnvironment] = React.useState<Environment>(defaultEnvironment);

  const jwtStorageKey = getEnvironmentTokenStorageKey(environment);

  // Hydrate JWT from localStorage when the environment key changes. Mirrors the
  // postcode panel's guard: excludes `jwtToken` from deps so it doesn't revert
  // edits on each keystroke.
  React.useEffect(() => {
    if (embedded) return; // parent supplies the token in embedded mode
    const savedToken = localStorage.getItem(jwtStorageKey) || '';
    if (savedToken) setJwtToken(savedToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtStorageKey, embedded]);

  function handleJwtTokenChange(value: string) {
    setJwtToken(value);
    const trimmed = value.trim();
    if (trimmed) localStorage.setItem(jwtStorageKey, trimmed);
    else localStorage.removeItem(jwtStorageKey);
  }

  // Network selection
  const [networks, setNetworks] = React.useState<UATNetwork[]>([]);
  const [networksLoading, setNetworksLoading] = React.useState(false);
  const [networkError, setNetworkError] = React.useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = React.useState('');
  const [selectedNetworkId, setSelectedNetworkId] = React.useState('');

  // Vehicle loading
  const [opsVehicles, setOpsVehicles] = React.useState<OpsVehicle[]>([]);
  const [vehiclesLoading, setVehiclesLoading] = React.useState(false);
  const [vehicleLoadWarning, setVehicleLoadWarning] = React.useState<string | null>(null);
  const [vehicleLoadError, setVehicleLoadError] = React.useState<string | null>(null);

  // Save / auto-match state
  const [saveNotice, setSaveNotice] = React.useState<string | null>(null);
  const [autoMatchNotice, setAutoMatchNotice] = React.useState<string | null>(null);
  const [lastAutoMatchedCount, setLastAutoMatchedCount] = React.useState(0);
  const autoLoadedNetworkKeysRef = React.useRef<Set<string>>(new Set());

  const selectedNetwork = networks.find((n) => n.productId === selectedProductId);

  // Effective auth/network: the parent's selection when embedded, else internal.
  const effJwt = auth?.jwtToken ?? jwtToken;
  const effEnvironment = auth?.environment ?? environment;
  const effProductId = auth?.productId ?? selectedProductId;
  const effNetworkUuid =
    auth?.networkUuid ??
    selectedNetwork?.networkUuid ??
    String(selectedNetwork?.defaultNetworkId ?? selectedNetworkId ?? '');
  const effNetworkLabel = auth?.networkLabel ?? selectedNetwork?.productName;

  function handleEnvironmentChange(next: Environment) {
    const token = localStorage.getItem(getEnvironmentTokenStorageKey(next)) || '';
    setEnvironment(next);
    setJwtToken(token);
    setNetworks([]);
    setSelectedProductId('');
    setSelectedNetworkId('');
    setOpsVehicles([]);
    setNetworkError(null);
    setVehicleLoadWarning(null);
    setVehicleLoadError(null);
    setSaveNotice(null);
    setAutoMatchNotice(null);
  }

  // ── Load networks ────────────────────────────────────────────────────────────
  const handleLoadNetworks = React.useCallback(async () => {
    if (!jwtToken.trim()) return;
    setNetworksLoading(true);
    setNetworkError(null);
    setNetworks([]);
    setSelectedProductId('');
    setSelectedNetworkId('');
    setOpsVehicles([]);
    setVehicleLoadWarning(null);
    try {
      const response = await fetchNetworks(jwtToken, environment);
      const list: UATNetwork[] = (response?.data?.entry ?? []).slice().sort((a, b) =>
        a.productName.localeCompare(b.productName, undefined, { sensitivity: 'base' }),
      );
      setNetworks(list);
      if (list.length === 0) {
        setNetworkError('No networks returned. Check your JWT token and environment.');
      }
    } catch (err) {
      setNetworkError(err instanceof Error ? err.message : 'Failed to load networks');
    } finally {
      setNetworksLoading(false);
    }
  }, [jwtToken, environment]);

  // Auto-load networks when a saved token is already present.
  React.useEffect(() => {
    if (embedded) return; // parent owns network selection in embedded mode
    const token = jwtToken.trim();
    if (!token || networksLoading) return;
    const key = `${environment}:${token}`;
    if (autoLoadedNetworkKeysRef.current.has(key)) return;
    autoLoadedNetworkKeysRef.current.add(key);
    void handleLoadNetworks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtToken, environment, networksLoading]);

  // ── Load vehicles + rehydrate persisted mapping ───────────────────────────────
  async function handleLoadVehicles() {
    if (!effJwt.trim() || !effProductId) return;
    const networkId = effNetworkUuid;
    setVehiclesLoading(true);
    setVehicleLoadError(null);
    setVehicleLoadWarning(null);
    setSaveNotice(null);
    setOpsVehicles([]);
    try {
      const result = await fetchOpsVehiclesForNetwork(
        effJwt,
        effProductId,
        networkId,
        effEnvironment,
        effNetworkLabel,
      );
      setOpsVehicles(result.vehicles);
      if (result.warning) setVehicleLoadWarning(result.warning);
      // Reconciliation (resolve seeded/persisted ids → labels, then auto-match)
      // is handled reactively by the effect below, so it runs regardless of
      // whether slots were seeded before or after the vehicles arrived.
    } catch (err) {
      setVehicleLoadError(err instanceof Error ? err.message : 'Failed to load vehicles');
    } finally {
      setVehiclesLoading(false);
    }
  }

  // ── Reactive back-fill: reconcile slots ↔ loaded vehicles ──────────────────────
  // Runs whenever the vehicle list arrives OR the slot set changes (e.g. slots
  // seeded after vehicles, or a preset applied). This removes the load-order
  // fragility where slots seeded before vehicles never got back-filled. Keyed on
  // the vehicle-list identity + slot COUNT (not slot content) so manual edits to
  // a single slot don't retrigger an auto-match that fights the user.
  React.useEffect(() => {
    if (opsVehicles.length === 0 || slots.length === 0) return;
    const persisted = getVehicleMapping(effEnvironment, effNetworkUuid);
    const labelled = slots.map((slot) => {
      const candidateId = slot.opsVehicleId || persisted[mappingKeyForSlot(slot)];
      if (!candidateId) return slot;
      const vehicle = opsVehicles.find((v) => v.id === candidateId);
      if (vehicle) {
        // Resolved → show rego + seating, never the raw id.
        return { ...slot, opsVehicleId: candidateId, opsVehicleLabel: describeOpsVehicle(vehicle) };
      }
      // Seeded (from sim) or stale persisted id not in this network → unmap so
      // the user (or auto-match) binds a real vehicle, rather than show a UUID.
      return { ...slot, opsVehicleId: '', opsVehicleLabel: '' };
    });
    const auto = autoMatchSlotsToVehicles(labelled, opsVehicles);
    // Only push an update if something actually changed, to avoid a render churn.
    const changed = auto.slots.some(
      (s, i) => s.opsVehicleId !== slots[i]?.opsVehicleId || s.opsVehicleLabel !== slots[i]?.opsVehicleLabel,
    );
    if (auto.matchedCount > 0) {
      setLastAutoMatchedCount(auto.matchedCount);
      setAutoMatchNotice(
        `Auto-matched ${auto.matchedCount} slot${auto.matchedCount === 1 ? '' : 's'} by rego / name / id.`,
      );
    }
    if (changed) setSlots(auto.slots);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opsVehicles, slots.length]);

  // Embedded mode: auto-load vehicles whenever the parent's auth/network changes,
  // so the user never re-enters credentials or clicks "Load vehicles" here.
  const autoLoadedVehicleKeyRef = React.useRef<string>('');
  React.useEffect(() => {
    if (!embedded) return;
    if (!effJwt.trim() || !effProductId || !effNetworkUuid || vehiclesLoading) return;
    const key = `${effEnvironment}:${effProductId}:${effNetworkUuid}`;
    if (autoLoadedVehicleKeyRef.current === key) return;
    autoLoadedVehicleKeyRef.current = key;
    void handleLoadVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, effJwt, effEnvironment, effProductId, effNetworkUuid, vehiclesLoading]);

  // ── Slot helpers ─────────────────────────────────────────────────────────────
  function updateSlot(localId: string, patch: Partial<VehicleSlot>) {
    setSlots(
      slots.map((s) => {
        if (s.localId !== localId) return s;
        const merged = { ...s, ...patch };
        if (patch.opsVehicleId !== undefined) {
          const vehicle = opsVehicles.find((v) => v.id === patch.opsVehicleId);
          merged.opsVehicleLabel = vehicle ? describeOpsVehicle(vehicle) : '';
        }
        return merged;
      }),
    );
    setSaveNotice(null);
  }

  function removeSlot(localId: string) {
    setSlots(slots.filter((s) => s.localId !== localId));
    setSaveNotice(null);
  }

  function addEmptySlot() {
    setSlots([...slots, buildEmptyVehicleSlot()]);
    setSaveNotice(null);
  }

  function applyPreset(preset: VehicleSlotPreset) {
    setSlots(preset.build());
    setSaveNotice(null);
    setAutoMatchNotice(null);
  }

  function handleAutoMatch() {
    const result = autoMatchSlotsToVehicles(slots, opsVehicles);
    setSlots(result.slots);
    setLastAutoMatchedCount(result.matchedCount);
    setAutoMatchNotice(
      result.matchedCount > 0
        ? `Auto-matched ${result.matchedCount} slot${result.matchedCount === 1 ? '' : 's'} by rego / name / id.`
        : 'No new auto-matches found. Map remaining slots manually below.',
    );
    setSaveNotice(null);
  }

  // ── Save (persist to localStorage + analytics) ────────────────────────────────
  function handleSaveMapping() {
    const networkId = effNetworkUuid;
    if (!networkId) {
      setSaveNotice('Select a network before saving the mapping.');
      return;
    }
    const mapping: Record<string, string> = {};
    for (const slot of slots) {
      if (slot.opsVehicleId) mapping[mappingKeyForSlot(slot)] = slot.opsVehicleId;
    }
    setVehicleMapping(effEnvironment, networkId, mapping);

    const mappedCount = slots.filter((s) => s.opsVehicleId).length;
    const unmappedCount = slots.length - mappedCount;

    trackAnalyticsEvent('fixed_route_vehicle_mapping_saved', {
      environment: effEnvironment,
      network_id: networkId,
      slot_count: slots.length,
      mapped_count: mappedCount,
      unmapped_count: unmappedCount,
      auto_matched_count: lastAutoMatchedCount,
      has_presets: presets.length > 0,
      timestamp: new Date().toISOString(),
    });

    setSaveNotice(`Saved mapping for ${mappedCount} vehicle${mappedCount === 1 ? '' : 's'}.`);
    onMappingSaved?.({ environment: effEnvironment, networkId, mappedCount, unmappedCount });
  }

  // ── Derived validation ────────────────────────────────────────────────────────
  const mappedCount = slots.filter((s) => s.opsVehicleId).length;
  const unmappedSlotCount = slots.length - mappedCount;
  const hasVehicleSource = opsVehicles.length > 0;
  const networkId = effNetworkUuid;

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Stack
      spacing={2.5}
      data-testid="fixed-route.vehicle-mapping-panel.root"
      data-mapped-count={mappedCount}
      data-unmapped-count={unmappedSlotCount}
    >
      {/* Credentials & environment — hidden in embedded mode (parent supplies auth) */}
      {!embedded && (
      <Stack spacing={1.5}>
        <Typography
          variant="subtitle2"
          component="div"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
        >
          <DirectionsBusOutlined fontSize="small" aria-hidden />
          Ops API credentials
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
          <TextField
            label="JWT token"
            type={showJwt ? 'text' : 'password'}
            value={jwtToken}
            onChange={(e) => handleJwtTokenChange(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 0 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowJwt((v) => !v)}
                    aria-label={showJwt ? 'Hide token' : 'Show token'}
                  >
                    {showJwt ? (
                      <VisibilityOffOutlined fontSize="small" />
                    ) : (
                      <VisibilityOutlined fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id="fr-vehicle-map-env-label">Environment</InputLabel>
            <Select
              labelId="fr-vehicle-map-env-label"
              value={environment}
              label="Environment"
              onChange={(e) => handleEnvironmentChange(e.target.value as Environment)}
              // Raise above a host dialog when embedded (no-op when not supplied).
              MenuProps={menuZIndexSx ? { sx: menuZIndexSx } : undefined}
            >
              {ENVIRONMENT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            disabled={!jwtToken.trim() || networksLoading}
            onClick={() => void handleLoadNetworks()}
            startIcon={
              networksLoading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <DownloadOutlined fontSize="small" />
              )
            }
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Load networks
          </Button>
        </Stack>
        {!jwtToken.trim() && (
          <Alert severity="info" icon={<WarningAmberOutlined fontSize="small" />}>
            No saved {getEnvironmentDisplayName(environment)} Ops token found. Paste a JWT above to
            load networks and vehicles.
          </Alert>
        )}
      </Stack>
      )}

      {/* Network / product selector — hidden in embedded mode (parent owns it) */}
      {!embedded && networks.length > 0 && (
        <Stack spacing={1.5}>
          <Typography
            variant="subtitle2"
            component="div"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
          >
            <HubOutlined fontSize="small" aria-hidden />
            Network / product
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel id="fr-vehicle-map-product-label">Product / network</InputLabel>
              <Select
                labelId="fr-vehicle-map-product-label"
                label="Product / network"
                value={selectedProductId}
                // Raise above a host dialog when embedded (no-op when not supplied).
                MenuProps={menuZIndexSx ? { sx: menuZIndexSx } : undefined}
                onChange={(e) => {
                  setSelectedProductId(e.target.value);
                  const net = networks.find((n) => n.productId === e.target.value);
                  setSelectedNetworkId(net?.networkUuid ?? String(net?.defaultNetworkId ?? ''));
                  setOpsVehicles([]);
                  setVehicleLoadError(null);
                  setVehicleLoadWarning(null);
                  setSaveNotice(null);
                }}
              >
                {networks.map((n) => (
                  <MenuItem key={n.productId} value={n.productId}>
                    {n.productName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="small"
              disabled={!selectedProductId || !selectedNetworkId || vehiclesLoading}
              onClick={() => void handleLoadVehicles()}
              startIcon={
                vehiclesLoading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <DownloadOutlined fontSize="small" />
                )
              }
              sx={{ whiteSpace: 'nowrap', flexShrink: 0, alignSelf: 'flex-start' }}
            >
              Load vehicles
            </Button>
          </Stack>
          {selectedNetwork && (
            <Typography variant="caption" color="text.secondary">
              {selectedNetwork.networkUuid
                ? `Network UUID: ${selectedNetwork.networkUuid}`
                : `Network dbId: ${selectedNetwork.defaultNetworkId} (UUID resolved on vehicle load)`}
              {selectedNetwork.timezone ? ` · ${selectedNetwork.timezone}` : ''}
            </Typography>
          )}
        </Stack>
      )}

      {/* Errors & warnings */}
      {networkError && (
        <Alert severity="error" sx={{ py: 0.5 }}>
          {networkError}
        </Alert>
      )}
      {vehicleLoadError && (
        <Alert
          severity="error"
          sx={{ py: 0.5, '& .MuiAlert-message': { whiteSpace: 'pre-wrap' } }}
        >
          {vehicleLoadError}
        </Alert>
      )}
      {vehicleLoadWarning && (
        <Alert severity="info" icon={<WarningAmberOutlined fontSize="inherit" />} sx={{ py: 0.5 }}>
          {vehicleLoadWarning}
        </Alert>
      )}
      {opsVehicles.length > 0 && (
        <Alert severity="success" sx={{ py: 0.5 }}>
          Loaded {opsVehicles.length} vehicle{opsVehicles.length === 1 ? '' : 's'} from Ops.
          {slots.length === 0 ? ' Add a slot (or apply a preset) to map them.' : ''}
        </Alert>
      )}

      <Divider />

      {/* Vehicle slot management */}
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography
            variant="subtitle2"
            component="div"
            sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}
          >
            <DirectionsBusOutlined fontSize="small" aria-hidden />
            Vehicle slots
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={addEmptySlot}
            startIcon={<AddOutlined fontSize="small" />}
          >
            Add slot
          </Button>
          <Tooltip title="Match slots to Ops vehicles by rego, name, or id" placement="top">
            <span>
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                onClick={handleAutoMatch}
                disabled={!hasVehicleSource || slots.length === 0}
                startIcon={<AutoFixHighOutlined fontSize="small" />}
              >
                Auto-match
              </Button>
            </span>
          </Tooltip>
          {presets.map((preset) => (
            <Tooltip
              key={preset.id}
              title={preset.description ?? `Apply the ${preset.label} preset`}
              enterTouchDelay={0}
              placement="top"
            >
              <Button variant="outlined" size="small" onClick={() => applyPreset(preset)}>
                {preset.label}
              </Button>
            </Tooltip>
          ))}
        </Stack>

        <Typography variant="caption" color="text.secondary">
          Map each slot to a real Ops vehicle. The mapping is saved locally (keyed by
          environment + network) so it survives a reload — an interim store to be replaced by an
          Ops external-reference field in a later phase.
        </Typography>

        {/* Auto-match / save notices */}
        {autoMatchNotice && (
          <Alert severity="info" sx={{ py: 0.5 }} icon={<AutoFixHighOutlined fontSize="inherit" />}>
            {autoMatchNotice}
          </Alert>
        )}
        {saveNotice && (
          <Alert severity="success" sx={{ py: 0.5 }} icon={<SaveOutlined fontSize="inherit" />}>
            {saveNotice}
          </Alert>
        )}

        {/* Validation warnings */}
        {slots.length > 0 && !hasVehicleSource && (
          <Alert severity="warning" sx={{ py: 0.5 }} icon={<WarningAmberOutlined fontSize="inherit" />}>
            No Ops vehicles loaded. Load vehicles above to map slots.
          </Alert>
        )}
        {slots.length > 0 && unmappedSlotCount > 0 && hasVehicleSource && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            {unmappedSlotCount} slot{unmappedSlotCount === 1 ? '' : 's'} not yet mapped to an Ops
            vehicle.
          </Alert>
        )}

        {/* Slots table */}
        {slots.length > 0 ? (
          <TableContainer
            data-testid="fixed-route.vehicle-mapping-panel.slot-list"
            sx={{ border: `1px solid ${theme.palette.surface.borderSubtle}`, borderRadius: 1 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.surface.elevated, 0.6) }}>
                  <TableCell sx={{ minWidth: 160 }}>Route</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Start window</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>End window</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Ops vehicle</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {slots.map((slot) => (
                  <TableRow key={slot.localId} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      <TextField
                        size="small"
                        variant="standard"
                        value={slot.name}
                        onChange={(e) => updateSlot(slot.localId, { name: e.target.value })}
                        placeholder="e.g. Route 7 AM"
                        fullWidth
                        inputProps={{ 'aria-label': 'Vehicle slot name' }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        variant="standard"
                        value={slot.startWindow}
                        onChange={(e) => updateSlot(slot.localId, { startWindow: e.target.value })}
                        placeholder="HH:MM"
                        inputProps={{ 'aria-label': 'Shift start window' }}
                        sx={{ width: 90 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        variant="standard"
                        value={slot.endWindow}
                        onChange={(e) => updateSlot(slot.localId, { endWindow: e.target.value })}
                        placeholder="HH:MM"
                        inputProps={{ 'aria-label': 'Shift end window' }}
                        sx={{ width: 90 }}
                      />
                    </TableCell>
                    <TableCell>
                      {hasVehicleSource ? (
                        <FormControl variant="standard" size="small" fullWidth>
                          <Select
                            value={slot.opsVehicleId}
                            onChange={(e) => updateSlot(slot.localId, { opsVehicleId: e.target.value })}
                            displayEmpty
                            // Raise the dropdown above a host dialog when embedded
                            // inside one (e.g. AttachOperationsModal's 2600 dialog),
                            // else it renders behind it. No-op when not supplied.
                            MenuProps={menuZIndexSx ? { sx: menuZIndexSx } : undefined}
                            renderValue={(val) =>
                              val ? (
                                <Chip
                                  label={(() => {
                                    const selected = opsVehicles.find((v) => v.id === val);
                                    return selected ? describeOpsVehicle(selected) : String(val);
                                  })()}
                                  size="small"
                                  sx={{ height: 20, fontSize: 11 }}
                                />
                              ) : (
                                <Typography variant="caption" color="text.disabled">
                                  — not mapped —
                                </Typography>
                              )
                            }
                            inputProps={{ 'aria-label': 'Ops vehicle assignment' }}
                          >
                            <MenuItem value="">
                              <em>Not mapped</em>
                            </MenuItem>
                            {opsVehicles.map((v) => (
                              <MenuItem key={v.id} value={v.id}>
                                {describeOpsVehicle(v)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          {slot.opsVehicleId
                            ? slot.opsVehicleLabel || slot.opsVehicleId
                            : '— load vehicles above —'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell padding="checkbox">
                      <IconButton
                        size="small"
                        onClick={() => removeSlot(slot.localId)}
                        aria-label="Remove slot"
                        sx={{ color: 'text.secondary' }}
                      >
                        <DeleteOutlineOutlined fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              py: 2.5,
              textAlign: 'center',
              border: `1px dashed ${theme.palette.surface.borderSubtle}`,
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No slots defined. Click <strong>Add slot</strong>
              {presets.length > 0 ? ' or apply a preset' : ''} to get started.
            </Typography>
          </Box>
        )}

        {/* Footer: summary chips + save */}
        {slots.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" useFlexGap>
            <Chip
              label={`${slots.length} slot${slots.length === 1 ? '' : 's'}`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${mappedCount} mapped`}
              size="small"
              color={mappedCount === slots.length ? 'success' : 'default'}
              variant="outlined"
            />
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveMapping}
              disabled={!networkId || mappedCount === 0}
              startIcon={<SaveOutlined fontSize="small" />}
            >
              Save mapping
            </Button>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
